// Staff "Partner Brands" admin client (Permissions.Partners.ManageBrands).
// Mirrors the C# admin DTOs in Core/DTOs/Partners/PartnerBrandDtos.cs.

import { api } from "@/api/client";

export type PartnerLogoMode = "WordmarkText" | "Asset";

/** The eight semantic color tokens (roles, not raw brand-color names). */
export interface PartnerBrandColors {
  primary: string;
  foreground: string;
  pressed: string;
  background: string;
  surface: string;
  accent: string;
  bodyText: string;
  hairline: string;
}

export interface PartnerBrandSummary {
  id: string;
  slug: string;
  displayName: string;
  referenceCode: string;
  logoMode: PartnerLogoMode;
  isActive: boolean;
  createdAt: string;
  /** Owning broker, or null. Mutually exclusive with ownerBrokerageId; both
   *  null = an ownerless brand (a general referral partner) — a supported
   *  state, not a gap. */
  ownerBrokerId?: string | null;
  /** Owning brokerage (the firm's shared site), or null. */
  ownerBrokerageId?: string | null;
  /** Owning BUYER TEAM (a Phoenix team's own site), or null. The third
   *  mutually-exclusive owner slot — and the only one that is not an external
   *  partner, which is why a completion on it becomes a Phoenix buyer lead
   *  routed to that team while every other branded completion does not. */
  ownerBuyerTeamId?: string | null;
  /** Display name of whichever owner is set. Null when ownerless — and also
   *  null when the owner id no longer resolves (soft-deleted record), which is
   *  why the list checks the ids, not this, to decide "Unowned". */
  ownerName?: string | null;
}

export interface PartnerBrand {
  id: string;
  slug: string;
  displayName: string;
  referenceCode: string;
  logoMode: PartnerLogoMode;
  wordmarkText?: string | null;
  logoAssetPath?: string | null;
  /** Time-limited read SAS URL for the uploaded logo (Asset mode), resolved
   *  server-side from logoAssetPath. Null in wordmark mode / when unresolved. */
  logoUrl?: string | null;
  colors: PartnerBrandColors;
  fontDisplay: string;
  fontBody: string;
  heroHeadline?: string | null;
  heroSubcopy?: string | null;
  contactCity?: string | null;
  contactPhone?: string | null;
  contactWebsite?: string | null;
  contactEmail?: string | null;
  isActive: boolean;
  /** Owning broker, or null. Mutually exclusive with ownerBrokerageId; both
   *  null = an ownerless brand (a general referral partner). */
  ownerBrokerId?: string | null;
  /** Owning brokerage (the firm's shared site), or null. */
  ownerBrokerageId?: string | null;
  /** Owning BUYER TEAM (a Phoenix team's own site), or null. The third
   *  mutually-exclusive owner slot — and the only one that is not an external
   *  partner, which is why a completion on it becomes a Phoenix buyer lead
   *  routed to that team while every other branded completion does not. */
  ownerBuyerTeamId?: string | null;
  createdAt: string;
  /** Per-portal switch for the two private analysis documents (default off). */
  analysisDocsEnabled: boolean;
  /** Optional variant drawn when the surface BEHIND the logo is light (the
   *  usual case). Null → the base logo is used on every background. Named by
   *  the background, not by the artwork's own color — the two are opposites. */
  logoAssetPathOnLight?: string | null;
  /** Optional variant drawn when the surface behind the logo is dark. */
  logoAssetPathOnDark?: string | null;
  logoUrlOnLight?: string | null;
  logoUrlOnDark?: string | null;
}

/** The two background-specific logo slots. Mirrors PartnerLogoVariants (C#). */
export type PartnerLogoVariant = "on-light" | "on-dark";

export interface CreatePartnerBrandRequest {
  slug: string;
  displayName: string;
  referenceCode: string;
  logoMode: PartnerLogoMode;
  wordmarkText?: string | null;
  logoAssetPath?: string | null;
  colors: PartnerBrandColors;
  fontDisplay: string;
  fontBody: string;
  heroHeadline?: string | null;
  heroSubcopy?: string | null;
  contactCity?: string | null;
  contactPhone?: string | null;
  contactWebsite?: string | null;
  contactEmail?: string | null;
  /** Owning broker, or null. Mutually exclusive with ownerBrokerageId; both
   *  null = an ownerless brand (a general referral partner). */
  ownerBrokerId?: string | null;
  /** Owning brokerage (the firm's shared site), or null. */
  ownerBrokerageId?: string | null;
  /** Owning BUYER TEAM (a Phoenix team's own site), or null. The third
   *  mutually-exclusive owner slot — and the only one that is not an external
   *  partner, which is why a completion on it becomes a Phoenix buyer lead
   *  routed to that team while every other branded completion does not. */
  ownerBuyerTeamId?: string | null;
  /** Per-portal switch for the two private analysis documents (default off). */
  analysisDocsEnabled?: boolean;
  /** When false, the brand is created inactive (invisible on the funnel until
   *  setActive flips it live). The creation wizard sends false; the single-page
   *  create omits it (server default true) → unchanged behavior. */
  activate?: boolean;
}

/** Edit payload — no slug (immutable after create), no activate (edit never
 *  changes active state; that's setActive's job). */
export type UpdatePartnerBrandRequest = Omit<
  CreatePartnerBrandRequest,
  "slug" | "activate"
>;

export interface PartnerBrandValidationMessage {
  severity: "error" | "warning";
  field: string;
  message: string;
}

export interface PartnerBrandValidationResult {
  hasErrors: boolean;
  messages: PartnerBrandValidationMessage[];
}

const BASE = "/partners/brands";

/** What is standing in the way of deleting a site. Mirrors the C# enum, which
 *  serializes as a string. */
export type PartnerBrandDeleteBlockerKind =
  | "InheritingBrokers"
  | "Submissions"
  | "Invites"
  | "Drafts"
  /** D2a — a buyer team's site is managed by the platform and never deletable
   *  by hand. Count is always 1, no names. */
  | "TeamOwned";

/** One reason a delete is refused. `names` is populated for InheritingBrokers
 *  only — and is capped, so trust `count` for the number and `names` for who to
 *  talk to first. */
export interface PartnerBrandDeleteBlocker {
  kind: PartnerBrandDeleteBlockerKind;
  count: number;
  names: string[];
}

/** Whether a site can be deleted, and precisely why not.
 *
 *  `refusalMessage` is composed SERVER-side and is the same string the refused
 *  DELETE throws, so the warning shown before the click and the error shown
 *  after are identical text. Render it verbatim — do not compose a second
 *  message on the client. */
export interface PartnerBrandDeleteEligibility {
  brandId: string;
  displayName: string;
  canDelete: boolean;
  blockers: PartnerBrandDeleteBlocker[];
  refusalMessage: string | null;
}

/** How an extraction ended. Serialized as a STRING (JsonStringEnumConverter is
 *  registered on the MVC options), so compare against these literals — not a
 *  numeric ordinal. All three arrive on a 200; only a malformed REQUEST (no
 *  file, empty, oversize, not a PDF) is a 400. */
export type PartnerBrandKitOutcome = "Succeeded" | "FoundNothing" | "Failed";

/** What was read out of the brand guide. Every field is independently optional
 *  — a guide that names only its colors returns nulls everywhere else. */
export interface PartnerBrandKitDraft {
  displayName: string | null;
  /** The eight semantic tokens, each null when the guide did not yield one.
   *  Colors are dropped server-side only for failing the #RRGGBB(AA) HEX
   *  SHAPE, and the count is named in `message`. Contrast is deliberately NOT
   *  checked here: a client's real brand colors failing 4.5:1 is a true fact
   *  about their brand, and the staff member should see it flagged by the
   *  existing guardrail on the colors step rather than have the value silently
   *  suppressed at extraction. */
  colors: { [K in keyof PartnerBrandColors]: string | null };
  fontDisplay: string | null;
  fontBody: string | null;
  heroHeadline: string | null;
  heroSubcopy: string | null;
  contactCity: string | null;
  contactPhone: string | null;
  contactWebsite: string | null;
}

/** The result of reading a partner's brand guide. `message` is staff-readable
 *  and already distinguishes "found nothing" from "could not read it" (and
 *  names any dropped colors) — render it rather than composing a second one. */
export interface PartnerBrandKitResult {
  outcome: PartnerBrandKitOutcome;
  message: string;
  draft: PartnerBrandKitDraft;
}

export const partnerBrandsApi = {
  list: () => api.get<PartnerBrandSummary[]>(BASE),
  get: (id: string) => api.get<PartnerBrand>(`${BASE}/${id}`),
  create: (body: CreatePartnerBrandRequest) => api.post<PartnerBrand>(BASE, body),
  update: (id: string, body: UpdatePartnerBrandRequest) =>
    api.put<PartnerBrand>(`${BASE}/${id}`, body),
  setActive: (id: string, isActive: boolean) =>
    api.post<PartnerBrand>(`${BASE}/${id}/active`, { isActive }),
  /** Upload/replace the brand's logo (multipart; PNG or JPEG, ≤ 5MB). Returns
   *  the brand with a resolved logoUrl and logoMode flipped to "Asset". */
  uploadLogo: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.upload<PartnerBrand>(`${BASE}/${id}/logo`, form);
  },
  /** Read a partner's brand guide (multipart; PDF only, ≤ 10MB) into a draft to
   *  prefill the wizard with. Persists NOTHING and needs no brand id — it runs
   *  before the row exists. Every outcome, including a failed read, comes back
   *  200: extraction is a convenience, so a failure must never block the site
   *  from being created by hand. */
  extractBrandKit: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.upload<PartnerBrandKitResult>(`${BASE}/extract-brand-kit`, form);
  },
  /** Remove the brand's logo (reverts to "WordmarkText" mode). */
  deleteLogo: (id: string) => api.delete<PartnerBrand>(`${BASE}/${id}/logo`),
  /** Upload/replace ONE background-specific variant. These have no field on the
   *  create/update payloads — this is their only writer, so a partial update
   *  can never silently clear them. */
  uploadLogoVariant: (id: string, variant: PartnerLogoVariant, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.upload<PartnerBrand>(`${BASE}/${id}/logo/${variant}`, form);
  },
  /** Remove ONE variant; the base logo and the other variant are untouched. */
  deleteLogoVariant: (id: string, variant: PartnerLogoVariant) =>
    api.delete<PartnerBrand>(`${BASE}/${id}/logo/${variant}`),
  /** Pure live-preview of the WCAG guardrail (no persistence). */
  validate: (colors: PartnerBrandColors) =>
    api.post<PartnerBrandValidationResult>(`${BASE}/validate`, colors),
  /** Whether this site can be deleted, and every blocker if not — including the
   *  NAMES of any brokers who inherit it. Read it BEFORE offering delete, so the
   *  confirm can state the consequence rather than asking "are you sure?". */
  deleteEligibility: (id: string) =>
    api.get<PartnerBrandDeleteEligibility>(`${BASE}/${id}/delete-eligibility`),
  /** Delete a site. Refused (400, carrying the same refusal message the
   *  eligibility read returns) whenever anything references the brand. There is
   *  deliberately no force flag — unpublish via `setActive(id, false)` is the
   *  answer for a site that did real work. */
  remove: (id: string) => api.delete<void>(`${BASE}/${id}`),
};
